import React, { useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import { useTranslation } from 'react-i18next'
import { getProtocolContainer } from '../../container/protocol-module'
import { IProtocolService, PROTOCOL_SERVICE_KEY } from '../../modules/protocols'
import { ImportError } from '../../modules/protocols/models/Protocol'

const protocolService = getProtocolContainer().get<IProtocolService>(PROTOCOL_SERVICE_KEY)

type Props = { onImported: () => void }

// Protocol import from Excel. #3196.
// EVERY validation error is shown with its row and column, which is how the
// backend returns them, so the sheet can be fixed in one go. The errors live in
// a dialog because the button now sits in the page header, where a panel of
// errors would push the whole table down.
export function ImportProtocol(props: Props) {
  const { t } = useTranslation()
  const input = useRef<HTMLInputElement>(null)

  const [errors, setErrors] = useState<ImportError[]>([])
  const [summary, setSummary] = useState<string>('')
  const [uploading, setUploading] = useState<boolean>(false)

  const choose = () => input.current?.click()
  const closeReport = () => {
    setErrors([])
    setSummary('')
  }

  const onFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setUploading(true)
    setErrors([])
    setSummary('')

    protocolService.importExcel(file, file.name.replace(/\.xlsx?$/i, '')).subscribe((r) => {
      setUploading(false)
      if (!r) return

      if (r.errors && r.errors.length > 0) {
        setErrors(r.errors)
        return
      }

      setSummary(t('importSummary', { ruleSets: r.ruleSets, rules: r.rules }))
      props.onImported()
    })

    // allows choosing the same file again
    e.target.value = ''
  }

  return (
    <>
      <Button
        variant="outlined"
        onClick={choose}
        disabled={uploading}
        startIcon={<FileUploadOutlinedIcon />}
        sx={{
          textTransform: 'none',
          borderRadius: 'var(--button-radius)',
          borderColor: 'var(--gray-border)',
          color: 'var(--gray-text)',
          '&:hover': { borderColor: 'var(--green-primary)', backgroundColor: 'transparent' },
        }}
      >
        {uploading ? t('uploading') : t('import')}
      </Button>
      <input ref={input} type="file" accept=".xlsx,.xls" hidden onChange={onFileChosen} />

      <Dialog open={Boolean(summary) || errors.length > 0} onClose={closeReport} maxWidth="md">
        <DialogTitle>{t('importFromExcel')}</DialogTitle>
        <DialogContent>
          {summary && <Alert severity="success">{summary}</Alert>}

          {errors.length > 0 && (
            <Box>
              <Alert severity="error" sx={{ mb: 1 }}>
                {t('importRejected', { n: errors.length })}
              </Alert>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('row')}</TableCell>
                    <TableCell>{t('column')}</TableCell>
                    <TableCell>{t('error')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {errors.map((e, i) => (
                    <TableRow key={i}>
                      <TableCell>{e.row || '-'}</TableCell>
                      <TableCell>{e.column || '-'}</TableCell>
                      <TableCell>{e.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('excelColumnsHint')}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
